// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { z } from 'zod';
import ConfigReader from './ConfigReader';

export enum Algorithm {
  RS256 = 'rs-256',
  RS256PASSWORDGRANT = 'rs-256-password-grant',
  HS256UNSAFE = 'hs-256-unsafe',
}

const hs256UnsafeSchema = z.object({
  algorithm: z.literal(Algorithm.HS256UNSAFE),
  secret: z.string(),
  audience: z.string(),
});
export type Hs256UnsafeAuth = z.infer<typeof hs256UnsafeSchema>;

export const rs256Schema = z.object({
  algorithm: z.literal(Algorithm.RS256),
  authority: z.string().url(),
  client_id: z.string(),
  audience: z.string(),
});
export type Rs256Auth = z.infer<typeof rs256Schema>;

// Same trust boundary as rs-256 (mock-oauth2 signs the token, Canton validates
// against its JWKS), but the frontend collects a username itself and exchanges
// it for a token via the OAuth2 Resource Owner Password grant. Preserves the
// one-field login UX for the local compose stack; not for real IdPs.
export const rs256PasswordGrantSchema = z.object({
  algorithm: z.literal(Algorithm.RS256PASSWORDGRANT),
  authority: z.string().url(),
  client_id: z.string(),
  audience: z.string(),
});
export type Rs256PasswordGrantAuth = z.infer<typeof rs256PasswordGrantSchema>;

const authSchema = z.discriminatedUnion('algorithm', [
  hs256UnsafeSchema,
  rs256Schema,
  rs256PasswordGrantSchema,
]);
export type AuthConf = z.infer<typeof authSchema>;

export enum Theme {
  DEFAULT = 'utilities',
}

const brandingSchema = z
  .object({
    app_name: z
      .string()
      .nullish()
      .transform((v) => (v === null || v === undefined || v === '' ? 'Utility' : v)),
    theme: z
      .union([z.nativeEnum(Theme), z.string()])
      .nullish()
      .transform((v) => (v === null || v === undefined || v === '' ? Theme.DEFAULT : (v as Theme))),
  })
  .optional()
  .default({
    app_name: 'Utility',
    theme: Theme.DEFAULT,
  });
export type BrandingConf = z.infer<typeof brandingSchema>;

const appSchema = z
  .object({
    mode: z.enum(['utility']).or(z.literal('').transform(() => 'utility')),
    utility_backend_url: z.string().optional().default('http://localhost:8080'),
    external_only: z.boolean().optional().default(true),
    deduplication_duration_in_seconds: z.number().optional().default(30),
    query_limit: z.number().optional().default(0), // 0 means no limit (will be extracted from server)
    version: z.string().optional().default('unknown'),
  })
  .optional()
  .default({
    mode: 'utility',
    utility_backend_url: 'http://localhost:8080',
    external_only: true,
    deduplication_duration_in_seconds: 30,
    query_limit: 0,
    version: 'unknown',
  });

export type AppConf = z.infer<typeof appSchema>;

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const settingsSchema = z
  .object({
    onboarding_request_timeout_milliseconds: z.number().optional().default(ONE_DAY_MS),
  })
  .optional()
  .default({});
export type SettingsConfig = z.infer<typeof settingsSchema>;

const configSpec = z.object({
  auth: authSchema,
  branding: brandingSchema,
  app: appSchema,
  settings: settingsSchema,
});
export type UtilityConfig = z.infer<typeof configSpec>;

export const isHs256UnsafeAuthConfig = (auth: AuthConf): auth is Hs256UnsafeAuth => {
  return auth.algorithm === Algorithm.HS256UNSAFE;
};
export const isRs256AuthConfig = (auth: AuthConf): auth is Rs256Auth => {
  return auth.algorithm === Algorithm.RS256;
};
export const isRs256PasswordGrantAuthConfig = (auth: AuthConf): auth is Rs256PasswordGrantAuth => {
  return auth.algorithm === Algorithm.RS256PASSWORDGRANT;
};

const configReader = new ConfigReader(configSpec);

export default configReader;
